<template>
  <div class="project-hero" data-qid="engagement-hero">
    <div class="project-hero__top">
      <div class="project-hero__main">
        <router-link v-if="project.project && project.project.id"
                     :to="{ name: 'projects.view', params: { slug: project.project.slug }}"
                     class="project-hero__crumb"
                     data-qid="engagement-hero-parent"
        >
          <font-awesome-icon icon="chevron-left" class="mr-1"/>{{ project.project.title }}
        </router-link>
        <div class="project-hero__eyebrow">
          <span v-for="(part, index) in eyebrow" :key="'eyebrow-' + index">{{ part }}</span>
        </div>
        <h1 class="project-hero__title" data-qid="p-view-page-title">{{ project.title }}</h1>

        <div class="project-hero__pills">
          <span class="hero-pill" :class="statusClass" data-qid="engagement-hero-status">
            <font-awesome-icon :icon="statusIcon"/>{{ $t('system.' + $options.filters.slugify(project.status || 'pending')) }}
          </span>
          <span class="hero-pill" v-if="access.role">
            <font-awesome-icon icon="users"/>{{ $t('projects.engagement.hero.your_role', { role: $t('projects.engagement.role.' + access.role) }) }}
          </span>
          <!-- Tags set in Edit project -->
          <span v-for="tag in tags"
                :key="'hero-tag-' + tag.name_translated"
                class="hero-pill hero-pill--tag"
                data-qid="engagement-hero-tag"
          >
            <font-awesome-icon icon="tag"/>{{ tag.name_translated }}
          </span>
          <span class="project-hero__team" v-if="team.length">
            <Avatar v-for="user in team.slice(0, 4)"
                    :key="'hero-user-' + user.id"
                    :object="user"
                    size="xs"
                    v-b-tooltip.hover
                    :title="user.name"
            ></Avatar>
            <span v-if="team.length > 4" class="project-hero__more">+{{ team.length - 4 }}</span>
          </span>
        </div>

        <div class="project-hero__facts">
          <div v-if="project.data_collection_end_date">
            {{ $t('projects.engagement.hero.documentation_due') }}
            <b>{{ project.data_collection_end_date | formatDate('DD MMM', true) }} · {{ relative(project.data_collection_end_date) }}</b>
          </div>
          <div v-if="project.end_date">
            {{ $t('projects.engagement.hero.project_due') }}
            <b>{{ project.end_date | formatDate('DD MMM', true) }} · {{ relative(project.end_date) }}</b>
          </div>
          <div v-if="showBudget">
            {{ $t('projects.engagement.budget.budget') }}
            <b :class="{'project-hero__over': totalSpent > totalBudget}">{{ $t('projects.engagement.hero.budget_value', { spent: hours(totalSpent), budget: hours(totalBudget) }) }}</b>
          </div>
        </div>

        <div class="project-hero__actions">
          <slot name="actions"></slot>
        </div>
      </div>

      <!-- Review progress -->
      <div class="project-hero__progress" v-if="review.total">
        <div class="project-hero__ring">
          <svg width="96" height="96" viewBox="0 0 96 96">
            <circle class="project-hero__ring-track"
                    cx="48"
                    cy="48"
                    r="40"
            />
            <!-- Light: finalized tasks; on top, the signed-off tasks -->
            <circle class="project-hero__ring-finalized"
                    cx="48"
                    cy="48"
                    r="40"
                    :stroke-dasharray="dash(finalizedPercent)"
                    transform="rotate(-90 48 48)"
            />
            <circle class="project-hero__ring-value"
                    cx="48"
                    cy="48"
                    r="40"
                    :stroke-dasharray="ringDash"
                    transform="rotate(-90 48 48)"
            />
          </svg>
          <b>{{ signedOffPercent }}%</b>
        </div>
        <div class="project-hero__progress-text">
          <div class="project-hero__progress-title">{{ $t('projects.engagement.hero.signed_off', { done: review.accepted, total: review.total }) }}</div>
          <div class="project-hero__legend">
            <span><i class="project-hero__swatch project-hero__swatch--finalized"></i>{{ $t('projects.engagement.hero.finalized_count', { done: finalized, total: review.total }) }}</span>
            <span><i class="project-hero__swatch"></i>{{ $t('projects.engagement.hero.signed_off_count', { done: review.accepted }) }}</span>
          </div>
        </div>
        <div class="project-hero__stats">
          <div :class="{'project-hero__stat--red': review.rejected}"><b>{{ review.rejected }}</b>{{ $t('projects.engagement.hero.rejected') }}</div>
          <div><b>{{ review.waiting }}</b>{{ $t('projects.engagement.hero.awaiting_sign_off') }}</div>
          <div><b>{{ engagement.unassigned_task_count || 0 }}</b>{{ $t('projects.engagement.hero.unassigned') }}</div>
        </div>
      </div>
    </div>

    <!-- Phases: always visible, a click opens the tab where the phase is worked on -->
    <div class="project-hero__stepper" data-qid="engagement-phase-stepper">
      <div v-for="(phase, index) in phases"
           :key="'hero-phase-' + phase"
           class="hero-step"
           :class="['hero-step--' + state(phase), {'cursor-pointer': !!tabFor(phase)}]"
           @click="tabFor(phase) && $emit('open-tab', tabFor(phase))"
           :data-qid="'engagement-stepper-' + phase"
      >
        <div class="hero-step__dot">
          <font-awesome-icon v-if="state(phase) === 'completed'" icon="check"/>
          <font-awesome-icon v-else-if="state(phase) === 'locked'" icon="lock"/>
          <span v-else>{{ index + 1 }}</span>
        </div>
        <div class="hero-step__text">
          <div class="hero-step__name">{{ $t('projects.engagement.phase.' + phase) }}</div>
          <div class="hero-step__sub">
            <span v-if="state(phase) === 'skipped'">{{ $t('projects.engagement.status.not_applicable') }}</span>
            <span v-else-if="state(phase) === 'locked'">{{ $t('projects.engagement.hero.locked') }}</span>
            <span v-else>{{ $t('projects.engagement.overview.phase_progress', { done: completed(phase), total: included(phase) }) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Avatar from '@/components/Avatar.vue';
import { PHASE_KEYS, PHASE_TABS, engagementPermissions, budgetEnabled, formatHours, phaseIncluded, phaseCompleted, phaseState } from '@/views/Projects/Engagement/engagement.js';

// Engagement header: client, period, status, role and team, key dates, review progress and the phases
export default {
  name: 'EngagementHero',
  props: {
    project: {
      type: Object,
      required: true
    },
    engagement: {
      type: Object,
      required: true
    }
  },
  components: {
    Avatar
  },
  data: function() {
    return {
      phases: PHASE_KEYS
    }
  },
  computed: {
    access() {
      return engagementPermissions(this.project);
    },
    team() {
      return this.project.users || [];
    },
    eyebrow() {
      let parts = [];
      const client = this.project.active_client || this.project.activeClient;
      if (client && client.name) {
        parts.push(client.name);
      }
      if (this.project.assessment && this.project.assessment.name) {
        parts.push(this.project.assessment.name);
      }
      if (this.project.project_type) {
        parts.push(this.project.project_type === 'type two' ? this.$t('system.type_two') : this.$t('system.type_one'));
      }
      if (this.project.scope_start_date) {
        const format = value => this.$options.filters.formatDate(value, 'DD MMM YYYY', true);
        parts.push(this.project.project_type === 'type one' || this.project.scope_start_date === this.project.scope_end_date
          ? format(this.project.scope_start_date)
          : format(this.project.scope_start_date) + ' – ' + format(this.project.scope_end_date));
      }
      return parts;
    },
    statusClass() {
      return {
        'hero-pill--warn': this.project.status === 'in progress' || this.project.status === 'pending',
        'hero-pill--ok': this.project.status === 'completed'
      };
    },
    statusIcon() {
      const icons = { completed: 'check-circle', closed: 'archive', 'in progress': 'clock', pending: 'hourglass-half' };
      return icons[this.project.status] || 'clock';
    },
    review() {
      return this.engagement.review || { total: 0, accepted: 0, rejected: 0, waiting: 0 };
    },
    signedOffPercent() {
      return this.review.total ? Math.round(this.review.accepted / this.review.total * 100) : 0;
    },
    ringDash() {
      return this.dash(this.signedOffPercent);
    },
    // Finalized tasks: signed off, waiting for sign-off or rejected after finalizing
    finalized() {
      return Math.min(this.review.total, (this.review.accepted || 0) + (this.review.waiting || 0) + (this.review.rejected || 0));
    },
    finalizedPercent() {
      return this.review.total ? Math.round(this.finalized / this.review.total * 100) : 0;
    },
    tags() {
      return this.project.tags_translated || [];
    },
    totalBudget() {
      return _.sum(_.values(this.engagement.budget || {}));
    },
    totalSpent() {
      return _.sum(_.values(this.engagement.spent || {}));
    },
    showBudget() {
      return budgetEnabled(this.project) && !!this.access.view_budget && this.totalBudget > 0;
    }
  },
  methods: {
    dash(percent) {
      const circumference = 2 * Math.PI * 40;
      return (circumference * percent / 100) + ' ' + circumference;
    },
    included(phase) {
      return phaseIncluded(this.engagement, phase);
    },
    completed(phase) {
      return phaseCompleted(this.engagement, phase);
    },
    state(phase) {
      return phaseState(this.engagement, phase);
    },
    // Associates don't have the Engagement acceptance and Planning tabs
    tabFor(phase) {
      const tab = PHASE_TABS[phase];
      return ['acceptance', 'planning'].includes(tab) && !this.access.manage ? null : tab;
    },
    hours(value) {
      return formatHours(value);
    },
    relative(value) {
      const moment = require('moment');
      const days = moment(String(value).substring(0, 10), 'YYYY-MM-DD').diff(moment().startOf('day'), 'days');
      if (days === 0) {
        return this.$t('projects.schedule.today');
      }
      return days > 0 ? this.$tc('projects.schedule.in_days', days, { count: days }) : this.$tc('projects.schedule.days_ago', -days, { count: -days });
    }
  }
}
</script>
